// Sheep Batches (processing batches) filters + sorts.
// Pure module: no React, no Supabase, no browser globals. SheepBatchesView owns
// rendering + data loading; this file owns deterministic filter/sort behavior.
//
// Right-sized for a small list (a handful to a few dozen batches): a single
// active sort rule {key, dir} and a flat set of filter dimensions — NOT the
// multi-rule / multi-group chip machinery the cattle-herds animal list needs.
//
// Predicate + comparator operate on the AUGMENTED batch row the view feeds to
// render/export: the persisted sheep_processing_batches columns (name, status,
// planned_process_date, actual_process_date, notes) plus the per-batch derived
// fields animal_count / total_live_weight / total_hanging_weight / yield_pct.

// Lifecycle order, not alphabetical: 'active' (displayed "In Process") sits
// between the booking and the finished batch. Migration 206 added it to the
// status CHECK; a batch reaches it by having sheep sent to it.
export const SHEEP_BATCH_STATUS_KEYS = Object.freeze(['planned', 'active', 'complete']);

export const SHEEP_BATCH_SORT_KEYS = Object.freeze(['batchName', 'status', 'plannedDate', 'animalCount', 'yieldPct']);

export const SHEEP_BATCH_FILTER_DIMENSIONS = Object.freeze([
  'textSearch',
  'status',
  'plannedDateRange',
  'animalCountRange',
]);

const STATUS_ORDER: readonly (string | null | undefined)[] = ['planned', 'active', 'complete'];

// The augmented batch row the view feeds to render/export: persisted columns
// plus the per-batch derived fields named in the header note.
export type SheepBatchRow = {
  id?: string | number | null;
  name?: string | null;
  status?: string | null;
  notes?: string | null;
  planned_process_date?: string | null;
  actual_process_date?: string | null;
  animal_count?: number | string | null;
  total_live_weight?: number | string | null;
  total_hanging_weight?: number | string | null;
  yield_pct?: number | string | null;
  sheep_detail?: readonly unknown[] | null;
};

export type SheepBatchNumericRange = {min?: number | null; max?: number | null};
export type SheepBatchDateRange = {after?: string | null; before?: string | null};

export type SheepBatchFilters = {
  textSearch?: string | null;
  status?: readonly (string | null | undefined)[] | null;
  plannedDateRange?: SheepBatchDateRange | null;
  animalCountRange?: SheepBatchNumericRange | null;
};

export type SheepBatchSortRule = {key?: string | null; dir?: string | null};

// Accepted for parity with the house pattern (todayMs, etc.) — see the `void
// ctx` statements below.
export type SheepBatchContext = Record<string, unknown>;

function normalizeStatus(row: SheepBatchRow | null | undefined): string {
  return row && row.status ? row.status : 'planned';
}

function animalCountOf(row: SheepBatchRow | null | undefined): number {
  // Prefer the augmented animal_count; fall back to the raw sheep_detail length
  // for un-augmented rows. Guard null/'' before Number() (Number(null) === 0).
  const raw = row ? row.animal_count : null;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  // Read through a local for the same reason `raw` above exists: the compiler
  // cannot carry Array.isArray()'s answer back onto `row.sheep_detail` through
  // the `row &&` guard. Same fallback, same result.
  const detail = row ? row.sheep_detail : null;
  return Array.isArray(detail) ? detail.length : 0;
}

function yieldPctOf(row: SheepBatchRow | null | undefined): number | null {
  // yield_pct is null when a batch has no live+hanging weights yet — treat that
  // (and undefined / '') as missing so it sorts to the end, NOT as 0. Number(null)
  // coerces to 0, so guard the empties before coercing.
  const raw = row ? row.yield_pct : null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function buildSheepBatchPredicate(filters: SheepBatchFilters | null, ctx: SheepBatchContext = {}) {
  const f = filters || {};
  // ctx is accepted for parity with the house pattern (todayMs, etc.); the
  // batch list filters are self-contained on the augmented row today.
  void ctx;

  return (row: SheepBatchRow | null | undefined) => {
    if (!row) return false;

    if (Array.isArray(f.status) && f.status.length > 0 && !f.status.includes(normalizeStatus(row))) return false;

    if (f.plannedDateRange && (f.plannedDateRange.after || f.plannedDateRange.before)) {
      const d = row.planned_process_date;
      if (!d) return false;
      if (f.plannedDateRange.after && d < f.plannedDateRange.after) return false;
      if (f.plannedDateRange.before && d > f.plannedDateRange.before) return false;
    }

    if (f.animalCountRange && (f.animalCountRange.min != null || f.animalCountRange.max != null)) {
      const count = animalCountOf(row);
      if (f.animalCountRange.min != null && count < f.animalCountRange.min) return false;
      if (f.animalCountRange.max != null && count > f.animalCountRange.max) return false;
    }

    if (typeof f.textSearch === 'string' && f.textSearch.trim()) {
      const q = f.textSearch.toLowerCase().trim();
      const fields = [row.name, row.id, row.notes].map((x) => (x == null ? '' : String(x).toLowerCase()));
      if (!fields.some((x) => x.includes(q))) return false;
    }

    return true;
  };
}

export function buildSheepBatchComparator(sortRule: SheepBatchSortRule | null, ctx: SheepBatchContext = {}) {
  void ctx;
  const rule =
    sortRule && sortRule.key && SHEEP_BATCH_SORT_KEYS.includes(sortRule.key)
      ? sortRule
      : {key: 'plannedDate', dir: 'desc'};
  const dir = rule.dir === 'desc' ? 'desc' : 'asc';
  return (a: SheepBatchRow, b: SheepBatchRow) => compareByKey(rule.key, a, b, dir);
}

function applyDir(cmp: number, dir: string): number {
  return dir === 'desc' ? -cmp : cmp;
}

function compareByKey(key: string | null | undefined, a: SheepBatchRow, b: SheepBatchRow, dir: string): number {
  switch (key) {
    case 'batchName': {
      const an = (a.name || '').trim();
      const bn = (b.name || '').trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return applyDir(an.localeCompare(bn, undefined, {numeric: true}), dir);
    }
    case 'status': {
      const ai = STATUS_ORDER.indexOf(normalizeStatus(a));
      const bi = STATUS_ORDER.indexOf(normalizeStatus(b));
      return applyDir((ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi), dir);
    }
    case 'plannedDate': {
      const ad = a.planned_process_date || null;
      const bd = b.planned_process_date || null;
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return applyDir(ad.localeCompare(bd), dir);
    }
    case 'animalCount':
      return applyDir(animalCountOf(a) - animalCountOf(b), dir);
    case 'yieldPct': {
      const ay = yieldPctOf(a);
      const by = yieldPctOf(b);
      if (ay == null && by == null) return 0;
      if (ay == null) return 1;
      if (by == null) return -1;
      return applyDir(ay - by, dir);
    }
    default:
      return 0;
  }
}
